import {useMemo} from 'react';
import {
  ICheckinCycleContent,
  ICurrentCheckinsInfoResponseData,
} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.interface';

export type ParticipantCycleCreator =
  | string
  | {
      id?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
    };

export interface ParticipantCycleMetadata {
  id?: string;
  cycleName?: string;
  createdBy?: ParticipantCycleCreator;
  content?: ICheckinCycleContent;
  params?: string[];
}

type ParticipantCheckinRow = Omit<
  ICurrentCheckinsInfoResponseData,
  'cycle' | 'cycleId'
> & {
  cycle?: string | ParticipantCycleMetadata;
  cycleId?: string | ParticipantCycleMetadata;
};

export interface ParticipantCheckinMetadataIndex {
  byCheckinId: Map<string, ParticipantCycleMetadata>;
  byCycleId: Map<string, ParticipantCycleMetadata>;
}

export interface ParticipantCheckinRouteSelection {
  checkinId?: string;
  mode?: 'view' | 'edit';
}

const isParticipantCheckinMode = (
  mode?: string | null,
): mode is ParticipantCheckinRouteSelection['mode'] =>
  mode === 'view' || mode === 'edit';

export const resolveParticipantCheckinRoute = (
  search: string,
  navigationState?: unknown,
): ParticipantCheckinRouteSelection => {
  const searchParams = new URLSearchParams(search);
  const state =
    navigationState && typeof navigationState === 'object'
      ? (navigationState as ParticipantCheckinRouteSelection)
      : undefined;
  const queryMode = searchParams.get('mode');

  return {
    checkinId: searchParams.get('checkinId') || state?.checkinId || undefined,
    mode: isParticipantCheckinMode(queryMode)
      ? queryMode
      : isParticipantCheckinMode(state?.mode)
      ? state?.mode
      : undefined,
  };
};

const emptyMetadataIndex = (): ParticipantCheckinMetadataIndex => ({
  byCheckinId: new Map(),
  byCycleId: new Map(),
});

const populatedCycle = (row?: ParticipantCheckinRow | null) => {
  if (!row) return undefined;
  if (row.cycle && typeof row.cycle === 'object') return row.cycle;
  if (row.cycleId && typeof row.cycleId === 'object') return row.cycleId;
  return undefined;
};

const getParticipantCycleId = (row?: ParticipantCheckinRow | null) => {
  if (!row) return undefined;
  if (typeof row.cycle === 'string') return row.cycle;
  if (row.cycle && typeof row.cycle === 'object') return row.cycle.id;
  if (typeof row.cycleId === 'string') return row.cycleId;
  return row.cycleId?.id;
};

const mergeCycleMetadata = (
  current?: ParticipantCycleMetadata,
  next?: ParticipantCycleMetadata,
): ParticipantCycleMetadata | undefined => {
  if (!current) return next;
  if (!next) return current;

  return {
    ...current,
    ...next,
    id: next.id || current.id,
    cycleName: next.cycleName || current.cycleName,
    createdBy: next.createdBy || current.createdBy,
    content: next.content || current.content,
    params: next.params || current.params,
  };
};

export const getParticipantCycleMetadata = (
  row?: ParticipantCheckinRow | null,
): ParticipantCycleMetadata | undefined => {
  if (!row) return undefined;

  const cycle = populatedCycle(row);
  const cycleId = cycle?.id || getParticipantCycleId(row);
  const cycleName = row.cycleName || cycle?.cycleName;

  if (!cycleId && !cycleName && !cycle?.createdBy && !cycle?.content) {
    return undefined;
  }

  return {
    ...cycle,
    id: cycleId,
    cycleName,
  };
};

export const buildParticipantCheckinMetadata = (
  rows: ParticipantCheckinRow[],
): ParticipantCheckinMetadataIndex =>
  rows.reduce<ParticipantCheckinMetadataIndex>((index, row) => {
    const metadata = getParticipantCycleMetadata(row);
    if (!metadata) return index;

    if (row.id) {
      index.byCheckinId.set(
        row.id,
        mergeCycleMetadata(index.byCheckinId.get(row.id), metadata) || metadata,
      );
    }

    if (metadata.id) {
      index.byCycleId.set(
        metadata.id,
        mergeCycleMetadata(index.byCycleId.get(metadata.id), metadata) ||
          metadata,
      );
    }

    return index;
  }, emptyMetadataIndex());

export const resolveParticipantCycleMetadata = (
  checkin: ICurrentCheckinsInfoResponseData | null | undefined,
  index: ParticipantCheckinMetadataIndex,
) => {
  if (!checkin) return undefined;

  const embedded = getParticipantCycleMetadata(
    checkin as ParticipantCheckinRow,
  );
  const byCheckin = checkin.id ? index.byCheckinId.get(checkin.id) : undefined;
  const cycleId =
    embedded?.id || getParticipantCycleId(checkin as ParticipantCheckinRow);
  const byCycle = cycleId ? index.byCycleId.get(cycleId) : undefined;

  return mergeCycleMetadata(mergeCycleMetadata(embedded, byCheckin), byCycle);
};

export const selectParticipantCheckin = (
  checkins: ICurrentCheckinsInfoResponseData[],
  checkinId?: string,
) =>
  (checkinId
    ? checkins.find((checkin) => checkin.id === checkinId)
    : checkins[0]) || null;

export const useParticipantCheckinMetadata = (
  checkins: ICurrentCheckinsInfoResponseData[],
  enabled = true,
) => {
  const metadata = useMemo(
    () =>
      enabled
        ? buildParticipantCheckinMetadata(checkins as ParticipantCheckinRow[])
        : emptyMetadataIndex(),
    [checkins, enabled],
  );

  return {
    metadata,
    isLoading: false,
  };
};
